/**
 * Environment configuration utility
 * Determines environment once at startup and provides consistent access
 */

export type Environment = 'local' | 'dev' | 'stg' | 'prd';

export interface EnvironmentConfig {
  env: Environment;
  apiUrl: string;
  websiteUrl: string;
  isDevelopment: boolean;
  isProduction: boolean;
  features: {
    enableAuth: boolean;
    enableDemo: boolean;
    enableFossilFills: boolean;
    enableAnalytics: boolean;
  };
}

class EnvironmentService {
  private config: EnvironmentConfig;

  constructor() {
    this.config = this.detectEnvironment();
    
    // Log environment on startup (useful for debugging)
    console.log(`[Environment] Detected: ${this.config.env}`, {
      hostname: window.location.hostname,
      apiUrl: this.config.apiUrl,
      features: this.config.features
    });
  }

  private detectEnvironment(): EnvironmentConfig {
    // Priority order:
    // 1. Explicit environment variable (if set during build)
    // 2. Hostname-based detection
    // 3. Default to local
    
    const hostname = window.location.hostname;
    let env: Environment;
    let apiUrl: string;
    let websiteUrl: string;

    // Check for explicit environment variable first
    if (process.env.REACT_APP_ENVIRONMENT) {
      env = process.env.REACT_APP_ENVIRONMENT as Environment;
      
      // Set URLs based on explicit environment
      switch (env) {
        case 'local':
          apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8017';
          websiteUrl = 'http://localhost:3000';
          break;
        case 'dev':
          apiUrl = process.env.REACT_APP_API_URL || 'https://api-dev.promptasaurus.ai';
          websiteUrl = 'https://dev.promptasaurus.ai';
          break;
        case 'stg':
          apiUrl = process.env.REACT_APP_API_URL || 'https://api-stg.promptasaurus.ai';
          websiteUrl = 'https://stg.promptasaurus.ai';
          break;
        case 'prd':
          apiUrl = process.env.REACT_APP_API_URL || 'https://api.promptasaurus.ai';
          websiteUrl = 'https://promptasaurus.ai';
          break;
        default:
          env = 'local';
          apiUrl = 'http://localhost:8017';
          websiteUrl = 'http://localhost:3000';
      }
    } else {
      // Detect from hostname
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        env = 'local';
        apiUrl = 'http://localhost:8017';
        websiteUrl = 'http://localhost:3000';
      } else if (hostname === 'dev.promptasaurus.ai') {
        env = 'dev';
        apiUrl = 'https://api-dev.promptasaurus.ai';
        websiteUrl = 'https://dev.promptasaurus.ai';
      } else if (hostname === 'stg.promptasaurus.ai') {
        env = 'stg';
        apiUrl = 'https://api-stg.promptasaurus.ai';
        websiteUrl = 'https://stg.promptasaurus.ai';
      } else if (hostname === 'promptasaurus.ai' || hostname === 'www.promptasaurus.ai') {
        env = 'prd';
        apiUrl = 'https://api.promptasaurus.ai';
        websiteUrl = 'https://promptasaurus.ai';
      } else {
        // Unknown hostname - assume local development
        console.warn(`[Environment] Unknown hostname: ${hostname}, defaulting to local`);
        env = 'local';
        apiUrl = 'http://localhost:8017';
        websiteUrl = `http://${hostname}:3000`;
      }
    }

    // Allow API URL override from environment variable
    if (process.env.REACT_APP_API_URL) {
      apiUrl = process.env.REACT_APP_API_URL;
    }

    return {
      env,
      apiUrl,
      websiteUrl,
      isDevelopment: env === 'local' || env === 'dev',
      isProduction: env === 'prd',
      features: {
        enableAuth: true, // Always enabled now
        enableDemo: true, // Demo available in all environments
        enableFossilFills: true, // Enabled in all environments now
        enableAnalytics: env === 'prd', // Only track in production
      }
    };
  }

  public getConfig(): EnvironmentConfig {
    return this.config;
  }

  public get env(): Environment {
    return this.config.env;
  }

  public get apiUrl(): string {
    return this.config.apiUrl;
  }

  public get websiteUrl(): string {
    return this.config.websiteUrl;
  }

  public get isDevelopment(): boolean {
    return this.config.isDevelopment;
  }

  public get isProduction(): boolean {
    return this.config.isProduction;
  }

  public get features(): EnvironmentConfig['features'] {
    return this.config.features;
  }

  /**
   * Helper to build API endpoints
   */
  public apiEndpoint(path: string): string {
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.apiUrl}${normalizedPath}`;
  }

  /**
   * Check if a feature is enabled
   */
  public isFeatureEnabled(feature: keyof EnvironmentConfig['features']): boolean {
    return this.config.features[feature];
  }
}

// Create singleton instance
export const environment = new EnvironmentService();

// Export convenience functions
export const getApiUrl = () => environment.apiUrl;
export const getEnvironment = () => environment.env;
export const isDevelopment = () => environment.isDevelopment;
export const isProduction = () => environment.isProduction;
export const apiEndpoint = (path: string) => environment.apiEndpoint(path);